/**
 * Словари статусов и форматов. Держим отдельно от компонентов:
 * их используют и кабинет партнёра, и админская модерация, и предпросмотр.
 */

export const CAMPAIGN_STATUS_LABELS = {
    draft: 'Черновик',
    moderation: 'На модерации',
    rejected: 'Отклонена',
    scheduled: 'Запланирована',
    active: 'Идут показы',
    paused: 'Остановлена',
    out_of_budget: 'Закончился бюджет',
    completed: 'Завершена',
    archived: 'В архиве',
};

export const CAMPAIGN_STATUS_COLORS = {
    draft: 'neutral',
    moderation: 'warning',
    rejected: 'danger',
    scheduled: 'primary',
    active: 'success',
    paused: 'neutral',
    out_of_budget: 'warning',
    completed: 'primary',
    archived: 'neutral',
};

export const MODERATION_STATUS_LABELS = {
    draft: 'Черновик',
    pending: 'На модерации',
    approved: 'Одобрено',
    rejected: 'Отклонено',
    changes_requested: 'Требуются правки',
};

export const MODERATION_STATUS_COLORS = {
    draft: 'neutral',
    pending: 'warning',
    approved: 'success',
    rejected: 'danger',
    changes_requested: 'warning',
};

export const FORMAT_LABELS = {
    deck_card: 'Карточка в колоде',
    banner_bottom: 'Нижний баннер',
    fullscreen: 'Полноэкранный',
    rewarded_video: 'Видео за награду',
    feed_native: 'Нативный в ленте',
    chat_list: 'Блок в чатах',
    profile_bottom: 'Под анкетой',
    search_inline: 'В результатах поиска',
    app_open: 'Заставка при запуске',
};

export const PRICING_LABELS = {
    cpc: 'За клики',
    cpm: 'За показы',
};

export const OBJECTIVE_LABELS = {
    traffic: 'Переходы',
    reach: 'Охват',
    conversions: 'Конверсии',
};

export const STRATEGY_LABELS = {
    manual_cpc: 'Ручное управление (за клики)',
    manual_cpm: 'Ручное управление (за показы)',
    auto_max_clicks: 'Максимум кликов',
    auto_max_impressions: 'Максимум показов по минимальной цене',
};

/** Стратегия жёстко связана с моделью оплаты — сервер это проверяет. */
export const STRATEGY_BY_PRICING = {
    cpc: ['manual_cpc', 'auto_max_clicks'],
    cpm: ['manual_cpm', 'auto_max_impressions'],
};

export const SYSTEM_PAUSE_LABELS = {
    no_funds: 'Недостаточно средств на счёте',
    daily_budget: 'Исчерпан дневной бюджет',
    total_budget: 'Исчерпан общий бюджет',
    account_suspended: 'Рекламный аккаунт приостановлен',
    date_end: 'Кампания завершена по дате',
};

/**
 * Какие действия допустимы в каком статусе.
 * Это зеркало CAMPAIGN_TRANSITIONS с сервера. Сервер всё равно проверяет
 * сам — здесь только чтобы не показывать заведомо неработающие кнопки.
 */
export const ALLOWED_ACTIONS = {
    draft: ['edit', 'submit', 'delete', 'archive'],
    moderation: ['archive'],
    rejected: ['edit', 'submit', 'archive'],
    scheduled: ['pause', 'archive'],
    active: ['pause', 'archive'],
    paused: ['edit', 'resume', 'submit', 'archive'],
    out_of_budget: ['pause', 'resume', 'archive'],
    completed: ['archive'],
    archived: [],
};

export const can = (status, action) => (ALLOWED_ACTIONS[status] || []).includes(action);

/** Совпадает с AD_REJECTION_REASON на сервере. */
export const REJECTION_REASONS = [
    { value: 'media_quality', label: 'Низкое качество изображения или видео' },
    { value: 'text_violation', label: 'Недопустимый текст' },
    { value: 'forbidden_category', label: 'Запрещённая категория рекламы' },
    { value: 'misleading', label: 'Вводит в заблуждение' },
    { value: 'landing_broken', label: 'Целевая ссылка не работает' },
    { value: 'adult', label: 'Недопустимый контент 18+' },
    { value: 'competitor', label: 'Реклама конкурирующего сервиса' },
    { value: 'format_mismatch', label: 'Не соответствует требованиям формата' },
    { value: 'no_ad_mark', label: 'Нет обязательной информации о рекламодателе' },
    { value: 'other', label: 'Другое' },
];

export const ACCOUNT_STATUS_LABELS = {
    active: 'Активен',
    suspended: 'Приостановлен',
    blocked: 'Заблокирован',
};

export const ADJUSTMENT_DIMENSION_LABELS = {
    gender: 'Пол',
    age: 'Возрастная группа',
    geo_city: 'Город',
    geo_region: 'Регион',
    platform: 'Платформа',
    schedule: 'День и час',
    paid_status: 'Статус подписки',
    placement: 'Площадка',
};

export const AGE_BUCKETS = ['18-24', '25-34', '35-44', '45-54', '55+'];

export const WEEKDAYS = [
    { value: '0', label: 'Понедельник' },
    { value: '1', label: 'Вторник' },
    { value: '2', label: 'Среда' },
    { value: '3', label: 'Четверг' },
    { value: '4', label: 'Пятница' },
    { value: '5', label: 'Суббота' },
    { value: '6', label: 'Воскресенье' },
];

export const STRATEGY_PHASE_LABELS = {
    learning: 'Обучение',
    running: 'Работает',
    capped: 'Упёрлась в ограничение',
    paused: 'Не применяется',
};

export const STRATEGY_PHASE_COLORS = {
    learning: 'warning',
    running: 'success',
    capped: 'primary',
    paused: 'neutral',
};

export const ROTATION_LABELS = {
    even: 'Равномерно',
    optimize: 'Оптимизация по отдаче',
};

/** Множитель на сервере ограничен 0.10–10.00, это те же −90%…+900%. */
export const multiplierToPercent = (multiplier) =>
    Math.round((Number(multiplier || 1) - 1) * 100);

export const percentToMultiplier = (percent) =>
    Number((1 + Number(percent || 0) / 100).toFixed(2));

export const formatPercentDelta = (percent) => {
    const value = Number(percent || 0);
    return `${value > 0 ? '+' : ''}${value}%`;
};

export const TX_TYPE_LABELS = {
    deposit: 'Пополнение',
    hold: 'Резерв',
    release: 'Снятие резерва',
    charge: 'Списание',
    refund: 'Возврат',
    correction: 'Корректировка',
};

export const TX_TYPE_COLORS = {
    deposit: 'success',
    hold: 'neutral',
    release: 'neutral',
    charge: 'primary',
    refund: 'warning',
    correction: 'warning',
};

/** Резерв и его снятие не меняют баланс — это движение внутри своих же денег. */
export const TX_AFFECTS_BALANCE = {
    deposit: 1,
    charge: -1,
    refund: -1,
    correction: 0,   // знак определяется самой суммой
    hold: 0,
    release: 0,
};

export const DEPOSIT_STATUS_LABELS = {
    created: 'Создано',
    pending: 'Ожидает оплаты',
    paid: 'Оплачено',
    canceled: 'Отменено',
    failed: 'Ошибка оплаты',
    refunded: 'Возвращено',
};

export const DEPOSIT_STATUS_COLORS = {
    created: 'neutral',
    pending: 'warning',
    paid: 'success',
    canceled: 'neutral',
    failed: 'danger',
    refunded: 'warning',
};

export const ACCOUNT_STATUS_COLORS = {
    active: 'success',
    suspended: 'warning',
    blocked: 'danger',
};

export const formatMoney = (value) => {
    const num = Number(value || 0);
    return num.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const formatInt = (value) => Number(value || 0).toLocaleString('ru-RU');